import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { Logger } from '../../logger/Logger';
import { toggleFullscreen } from '../gameRoot/useFullscreen';

import styles from './Fullscreen.module.css';

// Very Old Browsers and new iPads
interface WebkitDocument extends Document {
  webkitFullscreenElement?: HTMLElement;
  webkitExitFullscreen: () => Promise<void>;
}

const iOS_MinimalHeight = 25;
export const Fullscreen: FunctionComponent<{ isReadyToRender: boolean }> = ({ isReadyToRender }) => {
  const isAppleMobile = DeviceInfo.getPlatform().IS_IOS;
  const isOtherMobile = useRef(
    navigator.maxTouchPoints > 1 &&
      window.matchMedia(
        '(max-device-width: 500px) and (max-device-height: 1000px), (max-device-width: 1000px) and (max-device-height: 500px)'
      ).matches
  );
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);

  useEffect(() => {
    if (isAppleMobile || isOtherMobile.current) {
      const media = window.matchMedia('(orientation: landscape)');

      const toggleFullScreenClass = (): void => {
        setTimeout(() => {
          const isFullScreenEnable = window.screen.width - window.innerHeight <= iOS_MinimalHeight;
          !isFullScreenEnable && document.documentElement.scrollTo(0, 0);
          document.documentElement.classList.toggle('ios-fullscreen', isFullScreenEnable);
        }, 350);
      };

      const mediaFunc = function ({ matches }: { matches: boolean }, isInitial: boolean = false): void {
        Logger.logEvent('click', 'rotate_' + isInitial, Date.now(), 1, matches ? 'landscape' : 'portrait', false);

        if (matches) {
          setShowFullscreen(true);
          if (isAppleMobile) {
            document
              .querySelector<HTMLElement>('.' + styles.FullScreen)
              ?.style.setProperty('height', 'calc(100vh + 300px)');
            toggleFullScreenClass();
            window.addEventListener('resize', toggleFullScreenClass);
          }
        } else {
          setShowFullscreen(false);
          if (isAppleMobile) {
            document.querySelector<HTMLElement>('.' + styles.FullScreen)?.style.removeProperty('height');
            toggleFullScreenClass();
            window.scrollTo(0, 1);
            window.removeEventListener('resize', toggleFullScreenClass);
          } else if (document.fullscreenElement || (document as WebkitDocument).webkitFullscreenElement) {
            toggleFullscreen();
          }
        }
      };

      mediaFunc(media, true);
      media.addEventListener('change', mediaFunc);
      return () => {
        media.removeEventListener('change', mediaFunc);
        window.removeEventListener('resize', toggleFullScreenClass);
      };
    }
  }, []);

  if (window !== window.parent || !(isAppleMobile || isOtherMobile.current)) return null;

  const handleClick = (): void => {
    if (isOtherMobile.current) {
      toggleFullscreen();
    }
  };

  return (
    <div className={styles.FullScreen} style={{ zIndex: showFullscreen ? undefined : -999 }} onClick={handleClick}>
      {isReadyToRender ? <div className={`${styles.inner} ${isAppleMobile ? styles.swipeUp : styles.click}`} /> : null}
    </div>
  );
};
